import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminSchedule24Hour from './AdminSchedule24Hour';
import AdminWidgetConfig from './AdminWidgetConfig';
import AdminDiscordConfig from './AdminDiscordConfig';
import Toast from './Toast';

export default function AdminDashboard({ onLogout }) {
  const navigate = useNavigate();
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [expandedVoters, setExpandedVoters] = useState(new Set());
  const [resettingVotes, setResettingVotes] = useState(false);
  const [activeTab, setActiveTab] = useState('ideas'); // 'ideas', 'schedule', 'channels', 'widgets', 'discord', 'kanban', 'steam'
  
  // Channel management state
  const [channels, setChannels] = useState([]);
  const [channelsLoading, setChannelsLoading] = useState(false);
  const [channelsError, setChannelsError] = useState('');
  const [showCreateChannel, setShowCreateChannel] = useState(false);
  const [newChannelName, setNewChannelName] = useState('');
  const [newChannelPassword, setNewChannelPassword] = useState('');
  const [creatingChannel, setCreatingChannel] = useState(false);
  const [resettingPasswordFor, setResettingPasswordFor] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [deletingChannel, setDeletingChannel] = useState(null);

  // Widget timer state
  const [timerState, setTimerState] = useState(null);
  const [timerLoading, setTimerLoading] = useState(false);
  const [timerError, setTimerError] = useState('');
  const [timerHours, setTimerHours] = useState(0);
  const [timerMinutes, setTimerMinutes] = useState(0);
  const [updatingTimer, setUpdatingTimer] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const toastTimeoutRef = useRef(null);

  const showToast = useCallback((message) => {
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }
    setToastMessage(message);
    toastTimeoutRef.current = setTimeout(() => {
      setToastMessage('');
      toastTimeoutRef.current = null;
    }, 2500);
  }, []);

  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current);
      }
    };
  }, []);

  // Goals state
  const [goalsLoading, setGoalsLoading] = useState(false);
  const [goalsError, setGoalsError] = useState('');
  const [twitchGoals, setTwitchGoals] = useState([]);
  
  // Custom goals state (sub/follower goals stored in Redis)
  const [customGoals, setCustomGoals] = useState({ subGoal: null, followerGoal: null });
  const [customGoalsLoading, setCustomGoalsLoading] = useState(false);
  const [settingGoal, setSettingGoal] = useState(null);
  const [goalTargets, setGoalTargets] = useState({ subGoal: '', followerGoal: '' });
  const [widgetDebugData, setWidgetDebugData] = useState(null);
  const [streamDebugStatus, setStreamDebugStatus] = useState(null);
  const [debugJsonExpanded, setDebugJsonExpanded] = useState(false);

  // Twitch OAuth state
  const [twitchStatus, setTwitchStatus] = useState(null);
  const [twitchStatusLoading, setTwitchStatusLoading] = useState(false);
  const [twitchConnecting, setTwitchConnecting] = useState(false);

  // EventSub state
  const [eventSubList, setEventSubList] = useState([]);
  const [eventSubLoading, setEventSubLoading] = useState(false);
  const [eventSubError, setEventSubError] = useState('');
  const [registeringEventSub, setRegisteringEventSub] = useState(null);
  const [deletingEventSub, setDeletingEventSub] = useState(null);

  // Channel Point Rewards state
  const [rewards, setRewards] = useState([]);
  const [rewardsLoading, setRewardsLoading] = useState(false);
  const [rewardsError, setRewardsError] = useState('');
  const [copiedRewardId, setCopiedRewardId] = useState(null);

  // Overlay URLs state
  const [copiedOverlayUrl, setCopiedOverlayUrl] = useState(null);

  // Steam library state
  const [steamGames, setSteamGames] = useState([]);
  const [steamLoading, setSteamLoading] = useState(false);
  const [steamError, setSteamError] = useState('');

  // Fetch ideas from API
  const fetchIdeas = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      
      if (!token) {
        onLogout();
        return;
      }

      const response = await fetch('/api/admin?action=get-ideas', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setIdeas(data.ideas || []);
        setError('');
      } else {
        if (response.status === 401) {
          // Token expired or invalid
          localStorage.removeItem('adminToken');
          localStorage.removeItem('adminExpiresAt');
          onLogout();
          return;
        }
        setError(data.error || 'Failed to load ideas');
      }
    } catch (err) {
      console.error('Error fetching ideas:', err);
      setError('Failed to load ideas');
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  // Delete an idea
  const deleteIdea = async (ideaId) => {
    if (!window.confirm('Are you sure you want to delete this idea? This action cannot be undone.')) {
      return;
    }

    try {
      setDeletingId(ideaId);
      const token = localStorage.getItem('adminToken');
      
      const response = await fetch('/api/admin?action=delete-idea', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ action: 'delete-idea', ideaId })
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Remove the idea from local state
        setIdeas(prev => prev.filter(idea => idea.id !== ideaId));
      } else {
        if (response.status === 401) {
          // Token expired or invalid
          localStorage.removeItem('adminToken');
          localStorage.removeItem('adminExpiresAt');
          onLogout();
          return;
        }
        alert(data.error || 'Failed to delete idea');
      }
    } catch (err) {
      console.error('Error deleting idea:', err);
      alert('Failed to delete idea');
    } finally {
      setDeletingId(null);
    }
  };

  // Reset all votes
  const resetVotes = async () => {
    if (!window.confirm('Are you sure you want to reset ALL votes? This action cannot be undone.')) {
      return;
    }

    try {
      setResettingVotes(true);
      const token = localStorage.getItem('adminToken');
      
      const response = await fetch('/api/admin?action=reset-votes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ action: 'reset-votes' })
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Refresh ideas to show updated vote counts
        await fetchIdeas();
        alert(`Successfully reset votes for ${data.resetCount} ideas`);
      } else {
        if (response.status === 401) {
          // Token expired or invalid
          localStorage.removeItem('adminToken');
          localStorage.removeItem('adminExpiresAt');
          onLogout();
          return;
        }
        alert(data.error || 'Failed to reset votes');
      }
    } catch (err) {
      console.error('Error resetting votes:', err);
      alert('Failed to reset votes');
    } finally {
      setResettingVotes(false);
    }
  };

  // Toggle voter list expansion
  const toggleVoters = (ideaId) => {
    const newExpanded = new Set(expandedVoters);
    if (newExpanded.has(ideaId)) {
      newExpanded.delete(ideaId);
    } else {
      newExpanded.add(ideaId);
    }
    setExpandedVoters(newExpanded);
  };

  // Format timestamp to human-readable format
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  // Format vote timestamp
  const formatVoteTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    
    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString();
  };

  // Fetch channels from API
  const fetchChannels = useCallback(async () => {
    try {
      setChannelsLoading(true);
      const token = localStorage.getItem('adminToken');
      
      if (!token) {
        onLogout();
        return;
      }

      const response = await fetch('/api/admin?action=list-channels', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setChannels(data.channels || []);
        setChannelsError('');
      } else {
        if (response.status === 401) {
          localStorage.removeItem('adminToken');
          localStorage.removeItem('adminExpiresAt');
          onLogout();
          return;
        }
        setChannelsError(data.error || 'Failed to load channels');
      }
    } catch (err) {
      console.error('Error fetching channels:', err);
      setChannelsError('Failed to load channels');
    } finally {
      setChannelsLoading(false);
    }
  }, [onLogout]);

  // Create new channel
  const createChannel = async () => {
    if (!newChannelName.trim() || !newChannelPassword.trim()) {
      alert('Channel name and password are required');
      return;
    }

    try {
      setCreatingChannel(true);
      const token = localStorage.getItem('adminToken');
      
      const response = await fetch('/api/admin?action=create-channel', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          action: 'create-channel',
          channelName: newChannelName.trim().toLowerCase(),
          password: newChannelPassword
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setNewChannelName('');
        setNewChannelPassword('');
        setShowCreateChannel(false);
        await fetchChannels();
        alert(`Channel "${data.channelName}" created successfully!`);
      } else {
        if (response.status === 401) {
          localStorage.removeItem('adminToken');
          localStorage.removeItem('adminExpiresAt');
          onLogout();
          return;
        }
        alert(data.error || 'Failed to create channel');
      }
    } catch (err) {
      console.error('Error creating channel:', err);
      alert('Failed to create channel');
    } finally {
      setCreatingChannel(false);
    }
  };

  // Update channel password
  const updateChannelPassword = async (channelName) => {
    if (!newPassword.trim()) {
      alert('Password is required');
      return;
    }

    try {
      setResettingPasswordFor(channelName);
      const token = localStorage.getItem('adminToken');
      
      const response = await fetch('/api/admin?action=update-channel-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          action: 'update-channel-password',
          channelName: channelName,
          password: newPassword
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setNewPassword('');
        setResettingPasswordFor(null);
        await fetchChannels();
        alert(`Password updated for "${channelName}"`);
      } else {
        if (response.status === 401) {
          localStorage.removeItem('adminToken');
          localStorage.removeItem('adminExpiresAt');
          onLogout();
          return;
        }
        alert(data.error || 'Failed to update password');
      }
    } catch (err) {
      console.error('Error updating password:', err);
      alert('Failed to update password');
    } finally {
      setResettingPasswordFor(null);
    }
  };

  // Delete channel
  const deleteChannel = async (channelName) => {
    if (!window.confirm(`Are you sure you want to delete channel "${channelName}"? This will delete all schedule data and cannot be undone.`)) {
      return;
    }

    try {
      setDeletingChannel(channelName);
      const token = localStorage.getItem('adminToken');
      
      const response = await fetch('/api/admin?action=delete-channel', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          action: 'delete-channel',
          channelName: channelName
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        await fetchChannels();
        alert(`Channel "${channelName}" deleted successfully`);
      } else {
        if (response.status === 401) {
          localStorage.removeItem('adminToken');
          localStorage.removeItem('adminExpiresAt');
          onLogout();
          return;
        }
        alert(data.error || 'Failed to delete channel');
      }
    } catch (err) {
      console.error('Error deleting channel:', err);
      alert('Failed to delete channel');
    } finally {
      setDeletingChannel(null);
    }
  };

  // Fetch widget timer state
  // isAutoRefresh: if true, don't show loading state (for background polling)
  const fetchTimerState = useCallback(async (isAutoRefresh = false) => {
    try {
      if (!isAutoRefresh) {
        setTimerLoading(true);
      }
      setTimerError('');
      const token = localStorage.getItem('adminToken');
      
      if (!token) {
        onLogout();
        return;
      }

      const response = await fetch('/api/admin?action=get-widget-timer', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setTimerState(data);
        // Only update input fields on manual refresh, not auto-refresh
        // This prevents the inputs from being reset while user is typing
        if (!isAutoRefresh) {
          const displayMs = data.mode === 'countup'
            ? (data.displayTime ?? data.elapsedTime ?? 0)
            : (data.remainingTime ?? data.duration ?? 0);
          const totalMinutes = Math.floor(displayMs / (60 * 1000));
          setTimerHours(Math.floor(totalMinutes / 60));
          setTimerMinutes(totalMinutes % 60);
        }
        setTimerError('');
      } else {
        if (response.status === 401) {
          localStorage.removeItem('adminToken');
          localStorage.removeItem('adminExpiresAt');
          onLogout();
          return;
        }
        setTimerError(data.error || 'Failed to load timer state');
      }
    } catch (err) {
      console.error('Error fetching timer state:', err);
      setTimerError('Failed to load timer state');
    } finally {
      if (!isAutoRefresh) {
        setTimerLoading(false);
      }
    }
  }, [onLogout]);

  // Set timer duration
  const setTimer = async (startImmediately = false) => {
    const isCountUp = timerState?.mode === 'countup';

    if (timerHours < 0 || timerMinutes < 0) {
      setTimerError('Hours and minutes must be non-negative');
      return;
    }

    if (!isCountUp && timerHours === 0 && timerMinutes === 0) {
      setTimerError('Duration must be greater than 0');
      return;
    }

    try {
      setUpdatingTimer(true);
      setTimerError('');
      const token = localStorage.getItem('adminToken');
      
      const response = await fetch('/api/admin?action=set-widget-timer', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          action: 'set-widget-timer',
          hours: timerHours,
          minutes: timerMinutes,
          startImmediately: startImmediately
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        await fetchTimerState();
        if (startImmediately) {
          showToast('Timer set and started successfully');
        } else {
          showToast('Timer set successfully');
        }
      } else {
        if (response.status === 401) {
          localStorage.removeItem('adminToken');
          localStorage.removeItem('adminExpiresAt');
          onLogout();
          return;
        }
        setTimerError(data.error || 'Failed to set timer');
      }
    } catch (err) {
      console.error('Error setting timer:', err);
      setTimerError('Failed to set timer');
    } finally {
      setUpdatingTimer(false);
    }
  };

  // Set timer mode (countdown vs count-up)
  const setTimerModeSetting = async (mode) => {
    if (timerState?.mode === mode) return;

    try {
      setUpdatingTimer(true);
      setTimerError('');
      const token = localStorage.getItem('adminToken');

      const response = await fetch('/api/admin?action=set-widget-timer-mode', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          action: 'set-widget-timer-mode',
          mode
        })
      });

      const data = await response.json();

      if (data.success) {
        await fetchTimerState();
        showToast(mode === 'countup' ? 'Timer set to count up' : 'Timer set to count down');
      } else {
        if (response.status === 401) {
          localStorage.removeItem('adminToken');
          localStorage.removeItem('adminExpiresAt');
          onLogout();
          return;
        }
        setTimerError(data.error || 'Failed to set timer mode');
      }
    } catch (err) {
      console.error('Error setting timer mode:', err);
      setTimerError('Failed to set timer mode');
    } finally {
      setUpdatingTimer(false);
    }
  };

  // Sync timer from weekly / 24h schedule end and start immediately
  const syncTimerFromSchedule = async () => {
    try {
      setUpdatingTimer(true);
      setTimerError('');
      const token = localStorage.getItem('adminToken');

      const response = await fetch('/api/admin?action=sync-widget-timer-from-schedule', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          action: 'sync-widget-timer-from-schedule'
        })
      });

      const data = await response.json();

      if (data.success) {
        await fetchTimerState();
        showToast(data.sourceLabel
          ? `Timer synced: ${data.sourceLabel}`
          : 'Timer synced from weekly schedule and started');
      } else {
        if (response.status === 401) {
          localStorage.removeItem('adminToken');
          localStorage.removeItem('adminExpiresAt');
          onLogout();
          return;
        }
        setTimerError(data.error || 'Failed to sync timer from weekly schedule');
      }
    } catch (err) {
      console.error('Error syncing timer from weekly schedule:', err);
      setTimerError('Failed to sync timer from weekly schedule');
    } finally {
      setUpdatingTimer(false);
    }
  };

  // Update timer (start/stop/pause/resume/adjust)
  const updateTimer = async (action, adjustMinutes = null) => {
    try {
      setUpdatingTimer(true);
      setTimerError('');
      const token = localStorage.getItem('adminToken');
      
      const body = {
        action: 'update-widget-timer',
        timerAction: action
      };
      if (adjustMinutes !== null) {
        body.adjustMinutes = adjustMinutes;
      }
      
      const response = await fetch('/api/admin?action=update-widget-timer', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });
      
      const data = await response.json();
      
      if (data.success) {
        await fetchTimerState();
        const messages = {
          start: 'Timer started',
          stop: 'Timer stopped',
          pause: 'Timer paused',
          resume: 'Timer resumed',
          adjust: `Timer adjusted by ${adjustMinutes > 0 ? '+' : ''}${adjustMinutes} minutes`
        };
        showToast(messages[action] || 'Timer updated');
      } else {
        if (response.status === 401) {
          localStorage.removeItem('adminToken');
          localStorage.removeItem('adminExpiresAt');
          onLogout();
          return;
        }
        setTimerError(data.error || 'Failed to update timer');
      }
    } catch (err) {
      console.error('Error updating timer:', err);
      setTimerError('Failed to update timer');
    } finally {
      setUpdatingTimer(false);
    }
  };

  // Format milliseconds to HH:MM:SS
  const formatTime = (ms) => {
    if (ms <= 0) return '00:00:00';
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  // Fetch Twitch token status
  const fetchTwitchStatus = useCallback(async () => {
    try {
      setTwitchStatusLoading(true);
      const token = localStorage.getItem('adminToken');
      
      const response = await fetch('/api/admin?action=twitch-token-status', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setTwitchStatus(data);
      } else {
        setTwitchStatus({ configured: false, needsSetup: true });
      }
    } catch (err) {
      console.error('Error fetching Twitch status:', err);
      setTwitchStatus({ configured: false, error: err.message });
    } finally {
      setTwitchStatusLoading(false);
    }
  }, []);

  // Fetch goals from Twitch API
  const fetchGoals = useCallback(async () => {
    try {
      setGoalsLoading(true);
      setGoalsError('');

      const response = await fetch('/api/data?type=twitch-goals');
      const data = await response.json();
      
      if (data.success) {
        setTwitchGoals(data.goals || []);
        setGoalsError('');
      } else {
        setGoalsError(data.error || 'Failed to load goals from Twitch');
      }
    } catch (err) {
      console.error('Error fetching Twitch goals:', err);
      setGoalsError('Failed to load goals');
    } finally {
      setGoalsLoading(false);
    }
  }, []);

  // Start Twitch OAuth flow
  const connectTwitch = async () => {
    try {
      setTwitchConnecting(true);
      const token = localStorage.getItem('adminToken');
      const redirectUri = `${window.location.origin}/admin`;
      
      const response = await fetch(`/api/admin?action=twitch-auth-url&redirectUri=${encodeURIComponent(redirectUri)}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      
      if (data.success && data.authUrl) {
        // Store redirect URI for callback
        localStorage.setItem('twitchRedirectUri', redirectUri);
        // Redirect to Twitch
        window.location.href = data.authUrl;
      } else {
        alert(data.error || 'Failed to start Twitch authorization');
      }
    } catch (err) {
      console.error('Error starting Twitch auth:', err);
      alert('Failed to start Twitch authorization');
    } finally {
      setTwitchConnecting(false);
    }
  };

  // Handle OAuth callback (check for code in URL)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    
    if (code) {
      const handleCallback = async () => {
        try {
          const token = localStorage.getItem('adminToken');
          const redirectUri = localStorage.getItem('twitchRedirectUri') || `${window.location.origin}/admin`;
          
          const response = await fetch('/api/admin?action=twitch-auth-callback', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              action: 'twitch-auth-callback',
              code,
              redirectUri
            })
          });
          
          const data = await response.json();
          
          if (data.success) {
            alert(`Twitch connected successfully! Connected as: ${data.login}`);
            // Clean up URL and refresh status
            window.history.replaceState({}, document.title, window.location.pathname);
            localStorage.removeItem('twitchRedirectUri');
            fetchTwitchStatus();
            fetchGoals();
          } else {
            alert(data.error || 'Failed to complete Twitch authorization');
          }
        } catch (err) {
          console.error('Error handling Twitch callback:', err);
          alert('Failed to complete Twitch authorization');
        }
        
        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname);
      };
      
      handleCallback();
    }
  }, [fetchTwitchStatus, fetchGoals]);

  // Fetch custom goals (sub/follower goals from Redis)
  const fetchCustomGoals = useCallback(async () => {
    try {
      setCustomGoalsLoading(true);
      const channelName = 'itsflannelbeard'; // Default channel
      
      const [response, streamResponse] = await Promise.all([
        fetch(`/api/data?type=viewer-goals&channelName=${encodeURIComponent(channelName)}`),
        fetch(`/api/twitch-status?channelName=${encodeURIComponent(channelName)}`)
      ]);
      const data = await response.json();
      const streamData = await streamResponse.json().catch(() => null);
      
      setStreamDebugStatus(streamData);
      
      if (data) {
        setWidgetDebugData(data);
        setCustomGoals({
          subGoal: data.subGoal || { current: 0, target: 0 },
          followerGoal: data.followerGoal || { current: 0, target: 0 }
        });
        setGoalTargets({
          subGoal: data.subGoal?.target?.toString() || '',
          followerGoal: data.followerGoal?.target?.toString() || ''
        });
      }
    } catch (err) {
      console.error('Error fetching custom goals:', err);
    } finally {
      setCustomGoalsLoading(false);
    }
  }, []);

  // Set goal target
  const setGoalTarget = async (goalType) => {
    try {
      setSettingGoal(goalType);
      const token = localStorage.getItem('adminToken');
      const target = parseInt(goalTargets[goalType], 10);
      
      if (isNaN(target) || target < 0) {
        alert('Please enter a valid non-negative number');
        setSettingGoal(null);
        return;
      }

      const response = await fetch('/api/admin?action=set-goal', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          goalType,
          target,
          channelName: 'itsflannelbeard'
        })
      });

      const data = await response.json();
      
      if (data.success) {
        // Refresh custom goals
        await fetchCustomGoals();
        alert(`${goalType === 'subGoal' ? 'Sub' : 'Follower'} goal target set to ${target}`);
      } else {
        alert(`Failed to set goal: ${data.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error setting goal:', err);
      alert('Failed to set goal target');
    } finally {
      setSettingGoal(null);
    }
  };

  // Reset goal
  const resetGoal = async (goalType) => {
    if (!window.confirm(`Are you sure you want to reset the ${goalType === 'subGoal' ? 'sub' : 'follower'} goal count to 0?`)) {
      return;
    }

    try {
      setSettingGoal(goalType);
      const token = localStorage.getItem('adminToken');

      const response = await fetch('/api/admin?action=reset-goal', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          goalType,
          channelName: 'itsflannelbeard'
        })
      });

      const data = await response.json();
      
      if (data.success) {
        // Refresh custom goals
        await fetchCustomGoals();
        alert(`${goalType === 'subGoal' ? 'Sub' : 'Follower'} goal reset to 0`);
      } else {
        alert(`Failed to reset goal: ${data.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error resetting goal:', err);
      alert('Failed to reset goal');
    } finally {
      setSettingGoal(null);
    }
  };

  // Fetch EventSub subscriptions
  const fetchEventSub = useCallback(async () => {
    try {
      setEventSubLoading(true);
      setEventSubError('');
      const token = localStorage.getItem('adminToken');

      const response = await fetch('/api/admin?action=eventsub-list', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setEventSubList(data.subscriptions || []);
        setEventSubError('');
      } else {
        setEventSubError(data.error || 'Failed to load EventSub subscriptions');
      }
    } catch (err) {
      console.error('Error fetching EventSub:', err);
      setEventSubError('Failed to load subscriptions');
    } finally {
      setEventSubLoading(false);
    }
  }, []);

  // Register EventSub subscription
  const registerEventSub = async (eventType, rewardId = null) => {
    try {
      setRegisteringEventSub(eventType);
      const token = localStorage.getItem('adminToken');

      const response = await fetch('/api/admin?action=eventsub-register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          action: 'eventsub-register',
          eventType,
          rewardId
        })
      });

      const data = await response.json();

      if (data.success) {
        await fetchEventSub();
        alert(`Successfully registered ${eventType}`);
      } else {
        alert(data.error || 'Failed to register subscription');
      }
    } catch (err) {
      console.error('Error registering EventSub:', err);
      alert('Failed to register subscription');
    } finally {
      setRegisteringEventSub(null);
    }
  };

  // Delete EventSub subscription
  const deleteEventSub = async (subscriptionId, eventType) => {
    if (!window.confirm(`Delete ${eventType} subscription?`)) {
      return;
    }

    try {
      setDeletingEventSub(subscriptionId);
      const token = localStorage.getItem('adminToken');

      const response = await fetch('/api/admin?action=eventsub-delete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          action: 'eventsub-delete',
          subscriptionId
        })
      });

      const data = await response.json();

      if (data.success) {
        await fetchEventSub();
      } else {
        alert(data.error || 'Failed to delete subscription');
      }
    } catch (err) {
      console.error('Error deleting EventSub:', err);
      alert('Failed to delete subscription');
    } finally {
      setDeletingEventSub(null);
    }
  };

  // Register all EventSub subscriptions
  const registerAllEventSub = async () => {
    const unregistered = eventSubList.filter(sub => !sub.subscription);
    if (unregistered.length === 0) {
      alert('All subscriptions are already registered!');
      return;
    }

    for (const sub of unregistered) {
      await registerEventSub(sub.type);
    }
  };

  // Fetch Channel Point Rewards
  const fetchRewards = useCallback(async () => {
    try {
      setRewardsLoading(true);
      setRewardsError('');
      const token = localStorage.getItem('adminToken');

      const response = await fetch('/api/admin?action=list-rewards', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setRewards(data.rewards || []);
        setRewardsError('');
      } else {
        setRewardsError(data.error || 'Failed to load rewards');
      }
    } catch (err) {
      console.error('Error fetching rewards:', err);
      setRewardsError('Failed to load rewards');
    } finally {
      setRewardsLoading(false);
    }
  }, []);

  // Copy reward ID to clipboard
  const copyRewardId = async (rewardId) => {
    try {
      await navigator.clipboard.writeText(rewardId);
      setCopiedRewardId(rewardId);
      setTimeout(() => setCopiedRewardId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Copy overlay URL to clipboard
  const copyOverlayUrl = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedOverlayUrl(url);
      setTimeout(() => setCopiedOverlayUrl(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Fetch Steam games with 0 hours played
  const fetchSteamGames = useCallback(async () => {
    try {
      setSteamLoading(true);
      setSteamError('');
      const token = localStorage.getItem('adminToken');
      
      if (!token) {
        onLogout();
        return;
      }

      const response = await fetch('/api/admin?action=get-steam-games', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSteamGames(data.games || []);
        setSteamError('');
      } else {
        if (response.status === 401) {
          localStorage.removeItem('adminToken');
          localStorage.removeItem('adminExpiresAt');
          onLogout();
          return;
        }
        setSteamError(data.error || 'Failed to load Steam games');
      }
    } catch (err) {
      console.error('Error fetching Steam games:', err);
      setSteamError('Failed to load Steam games');
    } finally {
      setSteamLoading(false);
    }
  }, [onLogout]);

  // Check if session is still valid
  useEffect(() => {
    const expiresAt = localStorage.getItem('adminExpiresAt');
    if (expiresAt && Date.now() > parseInt(expiresAt)) {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminExpiresAt');
      onLogout();
      return;
    }

    fetchIdeas();
    if (activeTab === 'channels') {
      fetchChannels();
    }
    if (activeTab === 'steam') {
      fetchSteamGames();
    }
    
    let interval = null;
    if (activeTab === 'widgets') {
      fetchTimerState(false); // Initial load - show loading state
      fetchGoals();
      fetchTwitchStatus();
      fetchEventSub();
      fetchRewards();
      fetchChannels(); // Fetch channels for overlay URLs
      // Refresh timer state every 5 seconds when on widgets tab (1s polling
      // adds up against Vercel's edge request quota when the tab is left open)
      // Pass true for isAutoRefresh to prevent loading state flashing
      interval = setInterval(() => {
        if (document.hidden) return;
        fetchTimerState(true);
      }, 5000);
    }
    
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [onLogout, fetchIdeas, fetchChannels, fetchTimerState, fetchGoals, fetchTwitchStatus, fetchEventSub, fetchRewards, fetchSteamGames, activeTab]);

  return (
    <div className="min-h-screen bg-retro-bg retro-grid scanline p-2">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="retro-container p-2 sm:p-3 retro-glow mb-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
            <div>
              <h1 className="retro-title text-lg sm:text-xl md:text-2xl font-bold text-retro-cyan mb-1 sm:mb-2">
                ADMIN DASHBOARD
              </h1>
              <p className="retro-text text-retro-muted text-xs sm:text-sm">
                Manage submitted stream ideas and schedules
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
              <button
                onClick={() => navigate('/kanban')}
                className="px-4 py-2 rounded font-semibold transition-all duration-200 bg-retro-cyan text-retro-bg hover:bg-retro-cyan/80 border-2 border-retro-cyan hover:scale-105 active:scale-95 w-full sm:w-auto"
              >
                Kanban Board
              </button>
              {activeTab === 'ideas' && (
                <button
                  onClick={resetVotes}
                  disabled={resettingVotes}
                  className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-lg shadow-md transition-all duration-200 hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto text-sm sm:text-base"
                >
                  {resettingVotes ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                      Resetting...
                    </div>
                  ) : (
                    'RESET VOTES'
                  )}
                </button>
              )}
              <button
                onClick={onLogout}
                className="retro-button hover:scale-105 active:scale-95 w-full sm:w-auto"
              >
                LOGOUT
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="retro-container p-2 retro-glow mb-3">
          <div className="overflow-x-auto -mx-2 px-2 smooth-scroll" style={{ WebkitOverflowScrolling: 'touch' }}>
            <div className="flex gap-2 min-w-max sm:min-w-0">
              <button
                onClick={() => setActiveTab('ideas')}
                className={`px-3 sm:px-4 py-2 sm:py-2.5 rounded font-semibold transition-all duration-200 text-xs sm:text-sm md:text-base whitespace-nowrap min-h-[44px] flex items-center ${
                  activeTab === 'ideas'
                    ? 'bg-retro-cyan text-retro-bg'
                    : 'bg-retro-bg text-retro-cyan border border-retro-cyan hover:bg-retro-cyan hover:text-retro-bg'
                }`}
              >
                Ideas
              </button>
              <button
                onClick={() => setActiveTab('schedule')}
                className={`px-3 sm:px-4 py-2 sm:py-2.5 rounded font-semibold transition-all duration-200 text-xs sm:text-sm md:text-base whitespace-nowrap min-h-[44px] flex items-center ${
                  activeTab === 'schedule'
                    ? 'bg-retro-cyan text-retro-bg'
                    : 'bg-retro-bg text-retro-cyan border border-retro-cyan hover:bg-retro-cyan hover:text-retro-bg'
                }`}
              >
                24-Hour Schedule
              </button>
              <button
                onClick={() => {
                  setActiveTab('channels');
                  fetchChannels();
                }}
                className={`px-3 sm:px-4 py-2 sm:py-2.5 rounded font-semibold transition-all duration-200 text-xs sm:text-sm md:text-base whitespace-nowrap min-h-[44px] flex items-center ${
                  activeTab === 'channels'
                    ? 'bg-retro-cyan text-retro-bg'
                    : 'bg-retro-bg text-retro-cyan border border-retro-cyan hover:bg-retro-cyan hover:text-retro-bg'
                }`}
              >
                Channels
              </button>
              <button
                onClick={() => {
                  setActiveTab('widgets');
                  fetchTimerState();
                  fetchGoals();
                  fetchCustomGoals();
                  fetchEventSub();
                  fetchRewards();
                }}
                className={`px-3 sm:px-4 py-2 sm:py-2.5 rounded font-semibold transition-all duration-200 text-xs sm:text-sm md:text-base whitespace-nowrap min-h-[44px] flex items-center ${
                  activeTab === 'widgets'
                    ? 'bg-retro-cyan text-retro-bg'
                    : 'bg-retro-bg text-retro-cyan border border-retro-cyan hover:bg-retro-cyan hover:text-retro-bg'
                }`}
              >
                Widgets
              </button>
              <button
                onClick={() => setActiveTab('discord')}
                className={`px-3 sm:px-4 py-2 sm:py-2.5 rounded font-semibold transition-all duration-200 text-xs sm:text-sm md:text-base whitespace-nowrap min-h-[44px] flex items-center ${
                  activeTab === 'discord'
                    ? 'bg-retro-cyan text-retro-bg'
                    : 'bg-retro-bg text-retro-cyan border border-retro-cyan hover:bg-retro-cyan hover:text-retro-bg'
                }`}
              >
                Discord
              </button>
              <button
                onClick={() => navigate('/kanban')}
                className="px-3 sm:px-4 py-2 sm:py-2.5 rounded font-semibold transition-all duration-200 bg-retro-bg text-retro-cyan border border-retro-cyan hover:bg-retro-cyan hover:text-retro-bg text-xs sm:text-sm md:text-base whitespace-nowrap min-h-[44px] flex items-center"
              >
                Kanban
              </button>
              <button
                onClick={() => {
                  setActiveTab('steam');
                  fetchSteamGames();
                }}
                className={`px-3 sm:px-4 py-2 sm:py-2.5 rounded font-semibold transition-all duration-200 text-xs sm:text-sm md:text-base whitespace-nowrap min-h-[44px] flex items-center ${
                  activeTab === 'steam'
                    ? 'bg-retro-cyan text-retro-bg'
                    : 'bg-retro-bg text-retro-cyan border border-retro-cyan hover:bg-retro-cyan hover:text-retro-bg'
                }`}
              >
                Steam
              </button>
            </div>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'channels' && (
          <div className="retro-container p-3 retro-glow">
            <div className="flex items-center justify-between mb-3">
              <h2 className="retro-title text-xl font-bold text-retro-cyan">
                CHANNEL MANAGEMENT
              </h2>
              <div className="flex gap-2">
                <button
                  onClick={fetchChannels}
                  disabled={channelsLoading}
                  className="px-3 py-1 bg-retro-cyan text-retro-bg font-semibold rounded hover:bg-retro-cyan/80 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {channelsLoading ? 'Loading...' : 'Refresh'}
                </button>
                <button
                  onClick={() => setShowCreateChannel(!showCreateChannel)}
                  className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white font-semibold rounded transition-all duration-200"
                >
                  {showCreateChannel ? 'Cancel' : '+ Create Channel'}
                </button>
              </div>
            </div>

            {channelsError && (
              <div className="bg-red-900/20 border-2 border-red-500 rounded-lg p-3 mb-3">
                <p className="text-red-300">{channelsError}</p>
              </div>
            )}

            {/* Create Channel Form */}
            {showCreateChannel && (
              <div className="retro-card p-4 mb-3 border-2 border-retro-cyan">
                <h3 className="text-lg font-bold text-retro-text mb-3">Create New Channel</h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-retro-text text-sm font-semibold mb-1">
                      Channel Name (Twitch username)
                    </label>
                    <input
                      type="text"
                      value={newChannelName}
                      onChange={(e) => setNewChannelName(e.target.value)}
                      placeholder="e.g., itsflannelbeard"
                      className="w-full px-3 py-2 bg-retro-bg border-2 border-retro-cyan rounded text-retro-text"
                      disabled={creatingChannel}
                    />
                  </div>
                  <div>
                    <label className="block text-retro-text text-sm font-semibold mb-1">
                      Password
                    </label>
                    <input
                      type="password"
                      value={newChannelPassword}
                      onChange={(e) => setNewChannelPassword(e.target.value)}
                      placeholder="Set password for this channel"
                      className="w-full px-3 py-2 bg-retro-bg border-2 border-retro-cyan rounded text-retro-text"
                      disabled={creatingChannel}
                    />
                  </div>
                  <button
                    onClick={createChannel}
                    disabled={creatingChannel || !newChannelName.trim() || !newChannelPassword.trim()}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-semibold rounded transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {creatingChannel ? 'Creating...' : 'Create Channel'}
                  </button>
                </div>
              </div>
            )}

            {channelsLoading && (
              <div className="text-center py-4">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-retro-cyan border-t-transparent mb-2"></div>
                <p className="retro-text text-retro-muted">Loading channels...</p>
              </div>
            )}

            {!channelsLoading && channels.length === 0 && (
              <div className="text-center py-8">
                <p className="retro-text text-retro-muted text-lg mb-4">
                  No channels found.
                </p>
                <p className="retro-text text-retro-muted text-sm">
                  Create your first channel to get started.
                </p>
              </div>
            )}

            {!channelsLoading && channels.length > 0 && (
              <div className="space-y-3">
                {channels.map((channel) => (
                  <div key={channel.channelName} className="retro-card p-4 border border-gray-600">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-bold text-retro-text">
                            {channel.channelName}
                          </h3>
                          {channel.hasPassword && (
                            <span className="px-2 py-1 bg-green-600/20 text-green-400 text-xs rounded">
                              Has Password
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-retro-muted space-y-1">
                          {channel.date && (
                            <p>Schedule Date: {channel.date}</p>
                          )}
                          <p>Slots Filled: {channel.slotCount}</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {resettingPasswordFor === channel.channelName ? (
                          <div className="flex gap-2 items-center">
                            <input
                              type="password"
                              value={newPassword}
                              onChange={(e) => setNewPassword(e.target.value)}
                              placeholder="New password"
                              className="px-2 py-1 bg-retro-bg border border-retro-cyan rounded text-retro-text text-sm"
                            />
                            <button
                              onClick={() => updateChannelPassword(channel.channelName)}
                              disabled={!newPassword.trim()}
                              className="px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded disabled:opacity-50"
                            >
                              Save
                            </button>
                            <button
                              onClick={() => {
                                setResettingPasswordFor(null);
                                setNewPassword('');
                              }}
                              className="px-2 py-1 bg-gray-600 hover:bg-gray-700 text-white text-sm rounded"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <>
                            <button
                              onClick={() => setResettingPasswordFor(channel.channelName)}
                              className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded transition-all duration-200"
                            >
                              Reset Password
                            </button>
                            <button
                              onClick={() => deleteChannel(channel.channelName)}
                              disabled={deletingChannel === channel.channelName}
                              className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded transition-all duration-200 disabled:opacity-50"
                            >
                              {deletingChannel === channel.channelName ? 'Deleting...' : 'Delete'}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'widgets' && (
          <div className="retro-container p-3 retro-glow">
            <div className="flex items-center justify-between mb-3">
              <h2 className="retro-title text-xl font-bold text-retro-cyan">
                WIDGET MANAGEMENT
              </h2>
              <button
                onClick={fetchTimerState}
                disabled={timerLoading}
                className="px-3 py-1 bg-retro-cyan text-retro-bg font-semibold rounded hover:bg-retro-cyan/80 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {timerLoading ? 'Loading...' : 'Refresh'}
              </button>
            </div>

            {timerError && (
              <div className="bg-red-900/20 border-2 border-red-500 rounded-lg p-3 mb-3">
                <p className="text-red-300">{timerError}</p>
              </div>
            )}

            {/* Stream Timer Widget */}
            <div className="retro-card p-4 border-2 border-retro-cyan mb-4">
              <h3 className="text-lg font-bold text-retro-text mb-4">Stream Timer Widget</h3>
              <p className="text-sm text-retro-muted mb-4">
                Auto-starts when you go live. Countdown mode uses the weekly schedule end. Count-up mode tracks elapsed stream time from go-live.
              </p>

              {/* Timer Mode */}
              <div className="mb-4">
                <h4 className="text-md font-semibold text-retro-text mb-2">Timer Mode</h4>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setTimerModeSetting('countdown')}
                    disabled={updatingTimer || timerState?.mode === 'countdown'}
                    className={`px-4 py-2 font-semibold rounded transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                      timerState?.mode === 'countdown'
                        ? 'bg-retro-cyan text-retro-bg'
                        : 'bg-retro-surface text-retro-text hover:bg-retro-surface/80 border border-retro-cyan'
                    }`}
                  >
                    Count Down
                  </button>
                  <button
                    onClick={() => setTimerModeSetting('countup')}
                    disabled={updatingTimer || timerState?.mode === 'countup'}
                    className={`px-4 py-2 font-semibold rounded transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                      timerState?.mode === 'countup'
                        ? 'bg-retro-cyan text-retro-bg'
                        : 'bg-retro-surface text-retro-text hover:bg-retro-surface/80 border border-retro-cyan'
                    }`}
                  >
                    Count Up
                  </button>
                </div>
                <p className="text-xs text-retro-muted mt-2">
                  Count up shows elapsed stream time. OBS overlays can also force display with <code className="text-retro-text">?mode=countup</code> on the widget URL.
                </p>
              </div>
              
              {/* Current Timer Display */}
              {timerState && (
                timerState.isRunning
                || timerState.duration > 0
                || timerState.pausedAt !== null
                || timerState.displayTime > 0
              ) && (
                <div className="mb-4 p-3 bg-retro-surface rounded">
                  <div className="text-sm text-retro-muted mb-1">
                    {timerState.mode === 'countup' ? 'Elapsed:' : 'Remaining:'}
                  </div>
                  <div className="text-3xl font-mono font-bold text-retro-cyan">
                    {formatTime(timerState.mode === 'countup'
                      ? (timerState.displayTime ?? timerState.elapsedTime ?? 0)
                      : (timerState.remainingTime ?? 0))}
                  </div>
                  <div className="text-xs text-retro-muted mt-1">
                    Status: {timerState.isRunning ? 'Running' : 'Paused/Stopped'}
                  </div>
                  <div className="text-xs text-retro-muted mt-1">
                    Mode: {timerState.mode === 'countup' ? 'Count up' : 'Count down'}
                  </div>
                  {timerState.source && (
                    <div className="text-xs text-retro-muted mt-1">
                      Source: {timerState.manualOverride
                        ? 'Manual override'
                        : timerState.sourceLabel || timerState.source}
                    </div>
                  )}
                </div>
              )}

              {/* Sync from Schedule */}
              <div className="mb-4">
                <h4 className="text-md font-semibold text-retro-text mb-2">From Weekly Schedule</h4>
                <button
                  onClick={syncTimerFromSchedule}
                  disabled={updatingTimer}
                  className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Sync & Start from Weekly Schedule
                </button>
                <p className="text-xs text-retro-muted mt-2">
                  {timerState?.mode === 'countup'
                    ? 'Starts counting up from today\'s scheduled stream start in weeklySchedule.json.'
                    : 'Counts down to today\'s stream end in weeklySchedule.json.'}
                </p>
              </div>

              {/* Set Duration */}
              <div className="mb-4">
                <h4 className="text-md font-semibold text-retro-text mb-2">
                  {timerState?.mode === 'countup' ? 'Set Elapsed Offset' : 'Set Duration'}
                </h4>
                <div className="flex gap-3 items-center">
                  <div>
                    <label className="block text-retro-text text-sm font-semibold mb-1">
                      Hours
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={timerHours}
                      onChange={(e) => setTimerHours(parseInt(e.target.value) || 0)}
                      className="w-20 px-3 py-2 bg-retro-bg border-2 border-retro-cyan rounded text-retro-text"
                      disabled={updatingTimer}
                    />
                  </div>
                  <div>
                    <label className="block text-retro-text text-sm font-semibold mb-1">
                      Minutes
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="59"
                      value={timerMinutes}
                      onChange={(e) => setTimerMinutes(parseInt(e.target.value) || 0)}
                      className="w-20 px-3 py-2 bg-retro-bg border-2 border-retro-cyan rounded text-retro-text"
                      disabled={updatingTimer}
                    />
                  </div>
                  <div className="flex flex-col gap-2 pt-6">
                    <button
                      onClick={() => setTimer(false)}
                      disabled={updatingTimer || (timerState?.mode !== 'countup' && timerHours === 0 && timerMinutes === 0)}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {timerState?.mode === 'countup' ? 'Set Offset' : 'Set Duration'}
                    </button>
                    <button
                      onClick={() => setTimer(true)}
                      disabled={updatingTimer}
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-semibold rounded transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {timerState?.mode === 'countup' ? 'Start / Restart' : 'Set & Start'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Timer Controls */}
              {timerState && (
                timerState.mode === 'countup'
                || timerState.isRunning
                || timerState.duration > 0
                || timerState.pausedAt !== null
              ) && (
                <div className="mb-4">
                  <h4 className="text-md font-semibold text-retro-text mb-2">Timer Controls</h4>
                  <div className="flex flex-wrap gap-2">
                    {timerState.isRunning ? (
                      <>
                        <button
                          onClick={() => updateTimer('pause')}
                          disabled={updatingTimer}
                          className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white font-semibold rounded transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Pause
                        </button>
                        <button
                          onClick={() => updateTimer('stop')}
                          disabled={updatingTimer}
                          className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-semibold rounded transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Stop
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => updateTimer('start')}
                          disabled={updatingTimer}
                          className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-semibold rounded transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Start
                        </button>
                        {timerState.pausedAt !== null && (
                          <button
                            onClick={() => updateTimer('resume')}
                            disabled={updatingTimer}
                            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-semibold rounded transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Resume
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Adjust Time */}
              {timerState && (
                timerState.mode === 'countup'
                || timerState.isRunning
                || timerState.duration > 0
                || timerState.pausedAt !== null
              ) && (
                <div className="mb-4">
                  <h4 className="text-md font-semibold text-retro-text mb-2">Adjust Time</h4>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => updateTimer('adjust', 10)}
                      disabled={updatingTimer}
                      className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      +10 min
                    </button>
                    <button
                      onClick={() => updateTimer('adjust', 30)}
                      disabled={updatingTimer}
                      className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      +30 min
                    </button>
                    <button
                      onClick={() => updateTimer('adjust', 60)}
                      disabled={updatingTimer}
                      className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      +1 hour
                    </button>
                    <button
                      onClick={() => updateTimer('adjust', -10)}
                      disabled={updatingTimer}
                      className="px-3 py-2 bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      -10 min
                    </button>
                    <button
                      onClick={() => updateTimer('adjust', -30)}
                      disabled={updatingTimer}
                      className="px-3 py-2 bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      -30 min
                    </button>
                  </div>
                </div>
              )}

              {/* Widget URL */}
              <div className="mt-4 p-3 bg-retro-surface rounded">
                <div className="text-sm text-retro-muted mb-1">Widget URL for OBS:</div>
                <div className="text-xs font-mono text-retro-text break-all">
                  {window.location.origin}/widget-timer
                  {timerState?.mode === 'countup' ? '?mode=countup' : ''}
                </div>
              </div>
            </div>

            {/* Overlay URLs for OBS */}
            <div className="retro-card p-4 border-2 border-retro-cyan mb-4">
              <h3 className="text-lg font-bold text-retro-text mb-4">Overlay URLs for OBS</h3>
              
              {channelsLoading ? (
                <div className="text-center py-4">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-retro-cyan border-t-transparent"></div>
                  <p className="text-retro-muted mt-2">Loading channels...</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {channels.length === 0 ? (
                    <div className="p-3 bg-retro-surface rounded text-center">
                      <p className="text-retro-muted">No channels found. Create a channel first.</p>
                    </div>
                  ) : (
                    channels.map((channel) => {
                      const baseUrl = window.location.origin;
                      const channelName = channel.channelName;
                      const overlayUrls = [
                        { name: 'Full Overlay (with camera), theme from Widgets tab', path: `/overlay/${channelName}` },
                        { name: 'Full Overlay (no camera), theme from Widgets tab', path: `/overlay-no-camera/${channelName}` },
                        { name: 'Summer Overlay (with camera), always Summer', path: `/overlay-summer/${channelName}` },
                        { name: 'Summer Overlay (no camera), always Summer', path: `/overlay-summer-no-camera/${channelName}` },
                        { name: 'Schedule Overlay', path: `/widget/${channelName}` },
                        { name: 'Timer Overlay', path: `/timer/${channelName}` },
                      ];
                      
                      return (
                        <div key={channelName} className="p-3 bg-retro-surface rounded">
                          <h4 className="text-md font-semibold text-retro-cyan mb-3">{channelName}</h4>
                          <div className="space-y-2">
                            {overlayUrls.map((overlay) => {
                              const fullUrl = `${baseUrl}${overlay.path}`;
                              const isCopied = copiedOverlayUrl === fullUrl;
                              
                              return (
                                <div key={overlay.path} className="flex items-center gap-2 p-2 bg-retro-bg/50 rounded">
                                  <div className="flex-1 min-w-0">
                                    <div className="text-xs text-retro-muted mb-1">{overlay.name}</div>
                                    <div className="text-xs font-mono text-retro-text break-all">
                                      {fullUrl}
                                    </div>
                                  </div>
                                  <button
                                    onClick={() => copyOverlayUrl(fullUrl)}
                                    className={`px-3 py-1 text-xs font-semibold rounded transition-all duration-200 whitespace-nowrap ${
                                      isCopied
                                        ? 'bg-green-600 text-white'
                                        : 'bg-retro-cyan text-retro-bg hover:bg-retro-cyan/80'
                                    }`}
                                  >
                                    {isCopied ? 'Copied!' : 'Copy'}
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })
                  )}
                  
                  {/* Timer Widget (no channel) */}
                  <div className="p-3 bg-retro-surface rounded">
                    <h4 className="text-md font-semibold text-retro-cyan mb-3">Global Widgets</h4>
                    <div className="space-y-2">
                      {(() => {
                        const fullUrl = `${window.location.origin}/widget-timer`;
                        const isCopied = copiedOverlayUrl === fullUrl;
                        
                        return (
                          <div className="flex items-center gap-2 p-2 bg-retro-bg/50 rounded">
                            <div className="flex-1 min-w-0">
                              <div className="text-xs text-retro-muted mb-1">Timer Widget</div>
                              <div className="text-xs font-mono text-retro-text break-all">
                                {fullUrl}
                              </div>
                            </div>
                            <button
                              onClick={() => copyOverlayUrl(fullUrl)}
                              className={`px-3 py-1 text-xs font-semibold rounded transition-all duration-200 whitespace-nowrap ${
                                isCopied
                                  ? 'bg-green-600 text-white'
                                  : 'bg-retro-cyan text-retro-bg hover:bg-retro-cyan/80'
                              }`}
                            >
                              {isCopied ? 'Copied!' : 'Copy'}
                            </button>
                          </div>
                        );
                      })()}
                      {(() => {
                        const fullUrl = `${window.location.origin}/overlay-chat-test`;
                        const isCopied = copiedOverlayUrl === fullUrl;

                        return (
                          <div className="flex flex-wrap items-center gap-2 p-2 bg-retro-bg/50 rounded">
                            <div className="flex-1 min-w-0">
                              <div className="text-xs text-retro-muted mb-1">Chat stars test (mock users)</div>
                              <div className="text-xs font-mono text-retro-text break-all">
                                {fullUrl}
                              </div>
                            </div>
                            <a
                              href={fullUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-3 py-1 text-xs font-semibold rounded bg-retro-bg text-retro-cyan border border-retro-cyan hover:bg-retro-cyan hover:text-retro-bg transition-all duration-200 whitespace-nowrap"
                            >
                              Open
                            </a>
                            <button
                              type="button"
                              onClick={() => copyOverlayUrl(fullUrl)}
                              className={`px-3 py-1 text-xs font-semibold rounded transition-all duration-200 whitespace-nowrap ${
                                isCopied
                                  ? 'bg-green-600 text-white'
                                  : 'bg-retro-cyan text-retro-bg hover:bg-retro-cyan/80'
                              }`}
                            >
                              {isCopied ? 'Copied!' : 'Copy'}
                            </button>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Widget Configuration */}
            <AdminWidgetConfig onLogout={onLogout} />

            {/* Twitch Connection */}
            <div className="retro-card p-4 border-2 border-purple-500 mb-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-retro-text">Twitch Connection</h3>
                <button
                  onClick={fetchTwitchStatus}
                  disabled={twitchStatusLoading}
                  className="px-3 py-1 bg-purple-600 text-white font-semibold rounded hover:bg-purple-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  {twitchStatusLoading ? 'Checking...' : 'Check Status'}
                </button>
              </div>
              
              {twitchStatusLoading ? (
                <div className="text-center py-4">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-purple-500 border-t-transparent"></div>
                </div>
              ) : twitchStatus?.configured && twitchStatus?.valid ? (
                <div className="p-3 bg-green-900/20 border border-green-500 rounded">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-green-400 text-lg">✓</span>
                    <span className="text-green-300 font-semibold">Connected to Twitch</span>
                  </div>
                  <div className="text-sm text-retro-muted space-y-1">
                    {twitchStatus.login && <p>Account: <span className="text-retro-text">{twitchStatus.login}</span></p>}
                    {twitchStatus.scopes && (
                      <p>Scopes: <span className="text-retro-text text-xs">{twitchStatus.scopes.join(', ')}</span></p>
                    )}
                    {twitchStatus.timeUntilExpiry && (
                      <p>Token expires in: <span className="text-retro-text">
                        {Math.floor(twitchStatus.timeUntilExpiry / 1000 / 60)} minutes
                      </span> (auto-refreshes)</p>
                    )}
                  </div>
                  <button
                    onClick={connectTwitch}
                    disabled={twitchConnecting}
                    className="mt-3 px-3 py-1 bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold rounded transition-all duration-200 disabled:opacity-50"
                  >
                    {twitchConnecting ? 'Connecting...' : 'Re-authorize'}
                  </button>
                </div>
              ) : (
                <div className="p-3 bg-yellow-900/20 border border-yellow-500 rounded">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-yellow-400 text-lg">⚠</span>
                    <span className="text-yellow-300 font-semibold">Twitch Not Connected</span>
                  </div>
                  <p className="text-sm text-retro-muted mb-3">
                    Connect your Twitch account to enable Goals, EventSub, and other API features.
                  </p>
                  <button
                    onClick={connectTwitch}
                    disabled={twitchConnecting}
                    className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded transition-all duration-200 disabled:opacity-50 flex items-center gap-2"
                  >
                    {twitchConnecting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                        Connecting...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z"/>
                        </svg>
                        Connect Twitch Account
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>

            {/* Twitch Goals Widget */}
            <div className="retro-card p-4 border-2 border-retro-cyan">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-retro-text">Twitch Goals</h3>
                <button
                  onClick={fetchGoals}
                  disabled={goalsLoading}
                  className="px-3 py-1 bg-retro-cyan text-retro-bg font-semibold rounded hover:bg-retro-cyan/80 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  {goalsLoading ? 'Loading...' : 'Refresh'}
                </button>
              </div>
              
              {goalsError && (
                <div className="bg-red-900/20 border-2 border-red-500 rounded-lg p-3 mb-3">
                  <p className="text-red-300">{goalsError}</p>
                </div>
              )}

              {goalsLoading ? (
                <div className="text-center py-4">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-retro-cyan border-t-transparent"></div>
                </div>
              ) : twitchGoals.length === 0 ? (
                <div className="p-4 bg-retro-surface rounded text-center">
                  <p className="text-retro-muted mb-2">No active goals found on Twitch.</p>
                  <p className="text-sm text-retro-muted">
                    Create goals in your{' '}
                    <a 
                      href="https://dashboard.twitch.tv/u/itsflannelbeard/settings/channel/goals" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-retro-cyan hover:underline"
                    >
                      Twitch Dashboard → Creator Goals
                    </a>
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {twitchGoals.map((goal) => {
                    const percentage = goal.target > 0 ? Math.min((goal.current / goal.target) * 100, 100) : 0;
                    const goalTypeLabels = {
                      'follower': 'Followers',
                      'subscription': 'Subscriptions',
                      'subscription_count': 'Subscription Count',
                      'new_subscription': 'New Subscriptions',
                      'new_subscription_count': 'New Sub Count'
                    };
                    
                    return (
                      <div key={goal.id} className="p-3 bg-retro-surface rounded">
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <span className="text-sm font-semibold text-retro-cyan uppercase">
                              {goalTypeLabels[goal.type] || goal.type}
                            </span>
                            {goal.description && (
                              <p className="text-xs text-retro-muted mt-1">{goal.description}</p>
                            )}
                          </div>
                          <span className="text-lg font-bold text-retro-text">
                            {goal.current} / {goal.target}
                          </span>
                        </div>
                        
                        {/* Progress Bar */}
                        <div className="w-full h-3 bg-retro-bg rounded overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-retro-cyan to-green-400 transition-all duration-500"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                        <div className="flex justify-between mt-1">
                          <span className="text-xs text-retro-muted">
                            {percentage.toFixed(1)}% complete
                          </span>
                          <span className="text-xs text-retro-muted">
                            {goal.target - goal.current} to go
                          </span>
                        </div>
                      </div>
                    );
                  })}
                  
                  {/* Info */}
                  <div className="p-3 bg-retro-bg/30 rounded text-sm text-retro-muted">
                    <p>
                      Goals are managed in your{' '}
                      <a 
                        href="https://dashboard.twitch.tv/u/itsflannelbeard/settings/channel/goals" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-retro-cyan hover:underline"
                      >
                        Twitch Dashboard
                      </a>.
                      Data refreshes from Twitch API.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Custom Goals (Sub/Follower Goals) */}
            <div className="retro-card p-4 border-2 border-green-500 mb-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-retro-text">Custom Goals</h3>
                <button
                  onClick={fetchCustomGoals}
                  disabled={customGoalsLoading}
                  className="px-3 py-1 bg-green-600 text-white font-semibold rounded hover:bg-green-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  {customGoalsLoading ? 'Loading...' : 'Refresh'}
                </button>
              </div>

              {customGoalsLoading ? (
                <div className="text-center py-4">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-green-500 border-t-transparent"></div>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Sub Goal */}
                  <div className="p-3 bg-retro-surface rounded">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-semibold text-retro-cyan uppercase">Subscription Goal</h4>
                      <span className="text-lg font-bold text-retro-text">
                        {customGoals.subGoal?.current || 0} / {customGoals.subGoal?.target || 0}
                      </span>
                    </div>
                    
                    {customGoals.subGoal && customGoals.subGoal.target > 0 && (
                      <>
                        <div className="w-full h-3 bg-retro-bg rounded overflow-hidden mb-2">
                          <div 
                            className="h-full bg-gradient-to-r from-green-400 to-green-600 transition-all duration-500"
                            style={{ width: `${Math.min(((customGoals.subGoal.current || 0) / customGoals.subGoal.target) * 100, 100)}%` }}
                          />
                        </div>
                        <div className="flex justify-between text-xs text-retro-muted mb-3">
                          <span>
                            {((customGoals.subGoal.current || 0) / customGoals.subGoal.target * 100).toFixed(1)}% complete
                          </span>
                          <span>
                            {customGoals.subGoal.target - (customGoals.subGoal.current || 0)} to go
                          </span>
                        </div>
                      </>
                    )}
                    
                    <div className="flex gap-2">
                      <input
                        type="number"
                        min="0"
                        placeholder="Target"
                        value={goalTargets.subGoal}
                        onChange={(e) => setGoalTargets({ ...goalTargets, subGoal: e.target.value })}
                        className="flex-1 px-3 py-2 bg-retro-bg border border-retro-cyan text-retro-text rounded focus:outline-none focus:ring-2 focus:ring-retro-cyan"
                      />
                      <button
                        onClick={() => setGoalTarget('subGoal')}
                        disabled={settingGoal === 'subGoal'}
                        className="px-4 py-2 bg-green-600 text-white font-semibold rounded hover:bg-green-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {settingGoal === 'subGoal' ? 'Setting...' : 'Set Target'}
                      </button>
                      <button
                        onClick={() => resetGoal('subGoal')}
                        disabled={settingGoal === 'subGoal'}
                        className="px-4 py-2 bg-red-600 text-white font-semibold rounded hover:bg-red-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Reset
                      </button>
                    </div>
                  </div>

                  {/* Follower Goal */}
                  <div className="p-3 bg-retro-surface rounded">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-semibold text-retro-cyan uppercase">Follower Goal</h4>
                      <span className="text-lg font-bold text-retro-text">
                        {customGoals.followerGoal?.current || 0} / {customGoals.followerGoal?.target || 0}
                      </span>
                    </div>
                    
                    {customGoals.followerGoal && customGoals.followerGoal.target > 0 && (
                      <>
                        <div className="w-full h-3 bg-retro-bg rounded overflow-hidden mb-2">
                          <div 
                            className="h-full bg-gradient-to-r from-green-400 to-green-600 transition-all duration-500"
                            style={{ width: `${Math.min(((customGoals.followerGoal.current || 0) / customGoals.followerGoal.target) * 100, 100)}%` }}
                          />
                        </div>
                        <div className="flex justify-between text-xs text-retro-muted mb-3">
                          <span>
                            {((customGoals.followerGoal.current || 0) / customGoals.followerGoal.target * 100).toFixed(1)}% complete
                          </span>
                          <span>
                            {customGoals.followerGoal.target - (customGoals.followerGoal.current || 0)} to go
                          </span>
                        </div>
                      </>
                    )}
                    
                    <div className="flex gap-2">
                      <input
                        type="number"
                        min="0"
                        placeholder="Target"
                        value={goalTargets.followerGoal}
                        onChange={(e) => setGoalTargets({ ...goalTargets, followerGoal: e.target.value })}
                        className="flex-1 px-3 py-2 bg-retro-bg border border-retro-cyan text-retro-text rounded focus:outline-none focus:ring-2 focus:ring-retro-cyan"
                      />
                      <button
                        onClick={() => setGoalTarget('followerGoal')}
                        disabled={settingGoal === 'followerGoal'}
                        className="px-4 py-2 bg-green-600 text-white font-semibold rounded hover:bg-green-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {settingGoal === 'followerGoal' ? 'Setting...' : 'Set Target'}
                      </button>
                      <button
                        onClick={() => resetGoal('followerGoal')}
                        disabled={settingGoal === 'followerGoal'}
                        className="px-4 py-2 bg-red-600 text-white font-semibold rounded hover:bg-red-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Reset
                      </button>
                    </div>
                  </div>

                  {/* Info */}
                  <div className="p-3 bg-retro-bg/30 rounded text-sm text-retro-muted">
                    <p>
                      Custom goals track subscriptions and followers from EventSub events.
                      Set a target and the count will increment automatically when events are received.
                      Use the Reset button to reset the current count to 0 (target is preserved).
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* EventSub Subscriptions */}
            <div className="retro-card p-4 border-2 border-orange-500 mb-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-retro-text">EventSub Subscriptions</h3>
                <div className="flex gap-2">
                  <button
                    onClick={fetchEventSub}
                    disabled={eventSubLoading}
                    className="px-3 py-1 bg-orange-600 text-white font-semibold rounded hover:bg-orange-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                  >
                    {eventSubLoading ? 'Loading...' : 'Refresh'}
                  </button>
                  <button
                    onClick={registerAllEventSub}
                    disabled={eventSubLoading || registeringEventSub}
                    className="px-3 py-1 bg-green-600 text-white font-semibold rounded hover:bg-green-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                  >
                    Register All
                  </button>
                </div>
              </div>

              {eventSubError && (
                <div className="bg-red-900/20 border-2 border-red-500 rounded-lg p-3 mb-3">
                  <p className="text-red-300">{eventSubError}</p>
                </div>
              )}

              {eventSubLoading ? (
                <div className="text-center py-4">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-orange-500 border-t-transparent"></div>
                </div>
              ) : (
                <div className="space-y-2">
                  {eventSubList.map((sub) => (
                    <div key={sub.type} className="p-3 bg-retro-surface rounded flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className={`text-lg ${sub.subscription?.status === 'enabled' ? 'text-green-400' : sub.subscription ? 'text-yellow-400' : 'text-gray-500'}`}>
                            {sub.subscription?.status === 'enabled' ? '✓' : sub.subscription ? '⏳' : '○'}
                          </span>
                          <span className="font-semibold text-retro-text">{sub.description}</span>
                          {sub.requiresModerator && (
                            <span className="text-xs px-2 py-0.5 bg-purple-600/30 text-purple-300 rounded">Moderator</span>
                          )}
                        </div>
                        <div className="text-xs text-retro-muted mt-1 font-mono">{sub.type}</div>
                        {sub.subscription && (
                          <div className="text-xs text-retro-muted mt-1 space-y-1">
                            <div>
                              Status: <span className={sub.subscription.status === 'enabled' ? 'text-green-400' : 'text-yellow-400'}>
                                {sub.subscription.status}
                              </span>
                            </div>
                            {sub.type === 'channel.channel_points_custom_reward_redemption.add' && (
                              <div>
                                {sub.subscription.isAllRewards ? (
                                  <span className="text-green-300">✓ All channel point rewards</span>
                                ) : sub.subscription.rewardId ? (
                                  <span className="text-yellow-300">Filtered to specific reward: <code className="text-xs">{sub.subscription.rewardId.slice(0, 8)}...</code></span>
                                ) : (
                                  <span className="text-green-300">✓ All channel point rewards</span>
                                )}
                              </div>
                            )}
                            {sub.hasMultipleSubscriptions && (
                              <div className="text-yellow-300">
                                ⚠ {sub.subscriptionCount} subscriptions found (may include specific rewards)
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2">
                        {sub.subscription ? (
                          <button
                            onClick={() => deleteEventSub(sub.subscription.id, sub.type)}
                            disabled={deletingEventSub === sub.subscription.id}
                            className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded transition-all duration-200 disabled:opacity-50"
                          >
                            {deletingEventSub === sub.subscription.id ? '...' : 'Delete'}
                          </button>
                        ) : (
                          <button
                            onClick={() => registerEventSub(sub.type)}
                            disabled={registeringEventSub === sub.type}
                            className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded transition-all duration-200 disabled:opacity-50"
                          >
                            {registeringEventSub === sub.type ? '...' : 'Register'}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}

                  {eventSubList.length === 0 && !eventSubLoading && (
                    <div className="p-4 bg-retro-surface rounded text-center text-retro-muted">
                      {twitchStatus?.configured ? 'No subscription types loaded' : 'Connect Twitch first to manage subscriptions'}
                    </div>
                  )}

                  <div className="p-3 bg-retro-bg/30 rounded text-sm text-retro-muted mt-3">
                    <p>
                      EventSub webhooks enable real-time notifications for follows, subs, and cheers.
                      Callback URL must be publicly accessible.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Channel Point Rewards */}
            <div className="retro-card p-4 border-2 border-pink-500">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-retro-text">Channel Point Rewards</h3>
                <button
                  onClick={fetchRewards}
                  disabled={rewardsLoading}
                  className="px-3 py-1 bg-pink-600 text-white font-semibold rounded hover:bg-pink-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  {rewardsLoading ? 'Loading...' : 'Refresh'}
                </button>
              </div>

              {rewardsError && (
                <div className="bg-red-900/20 border-2 border-red-500 rounded-lg p-3 mb-3">
                  <p className="text-red-300">{rewardsError}</p>
                </div>
              )}

              {rewardsLoading ? (
                <div className="text-center py-4">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-pink-500 border-t-transparent"></div>
                </div>
              ) : rewards.length === 0 ? (
                <div className="p-4 bg-retro-surface rounded text-center">
                  <p className="text-retro-muted mb-2">No channel point rewards found.</p>
                  <p className="text-sm text-retro-muted">
                    Create rewards in your{' '}
                    <a 
                      href="https://dashboard.twitch.tv/viewer-rewards/channel-points/rewards" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-pink-400 hover:underline"
                    >
                      Twitch Dashboard → Channel Points
                    </a>
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {rewards.map((reward) => (
                    <div 
                      key={reward.id} 
                      className="p-3 bg-retro-surface rounded flex items-center justify-between"
                      style={{ borderLeft: `4px solid ${reward.backgroundColor || '#9147ff'}` }}
                    >
                      <div className="flex items-center gap-3 flex-1">
                        {reward.image && (
                          <img src={reward.image} alt="" className="w-8 h-8 rounded" />
                        )}
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-retro-text">{reward.title}</span>
                            <span className="text-sm text-retro-cyan">{reward.cost} pts</span>
                            {!reward.isEnabled && (
                              <span className="text-xs px-2 py-0.5 bg-gray-600/50 text-gray-400 rounded">Disabled</span>
                            )}
                            {reward.isPaused && (
                              <span className="text-xs px-2 py-0.5 bg-yellow-600/30 text-yellow-300 rounded">Paused</span>
                            )}
                          </div>
                          {reward.prompt && (
                            <p className="text-xs text-retro-muted mt-1 line-clamp-1">{reward.prompt}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <code className="text-xs text-retro-muted bg-retro-bg px-2 py-1 rounded font-mono max-w-[120px] truncate">
                          {reward.id}
                        </code>
                        <button
                          onClick={() => copyRewardId(reward.id)}
                          className={`px-2 py-1 text-xs font-semibold rounded transition-all duration-200 ${
                            copiedRewardId === reward.id
                              ? 'bg-green-600 text-white'
                              : 'bg-retro-bg text-retro-text hover:bg-retro-cyan hover:text-retro-bg'
                          }`}
                        >
                          {copiedRewardId === reward.id ? 'Copied!' : 'Copy ID'}
                        </button>
                      </div>
                    </div>
                  ))}

                  <div className="p-3 bg-retro-bg/30 rounded text-sm text-retro-muted mt-3">
                    <p>
                      Copy reward IDs for use with EventSub channel point redemption subscriptions.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Widget Debug Panel */}
            <div className="retro-card p-4 border-2 border-amber-500 mb-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-retro-text">Widget Debug</h3>
                <button
                  onClick={fetchCustomGoals}
                  disabled={customGoalsLoading}
                  className="px-3 py-1 bg-amber-600 text-white font-semibold rounded hover:bg-amber-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  {customGoalsLoading ? 'Loading...' : 'Refresh'}
                </button>
              </div>

              {!widgetDebugData ? (
                <div className="text-center py-4 text-retro-muted">
                  No debug data loaded yet. Click Refresh.
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Stream Status */}
                  <div className="p-3 bg-retro-surface rounded">
                    <h4 className="text-sm font-semibold text-amber-400 uppercase mb-2">Stream Status</h4>
                    {streamDebugStatus ? (
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <span className="text-retro-muted">Live:</span>
                        <span className={streamDebugStatus.isLive ? 'text-green-400 font-bold' : 'text-red-400 font-bold'}>
                          {streamDebugStatus.isLive ? 'YES' : 'NO'}
                        </span>
                        <span className="text-retro-muted">Stream ID:</span>
                        <code className="text-retro-text text-xs bg-retro-bg px-1 rounded">
                          {streamDebugStatus.streamId || 'N/A'}
                        </code>
                        <span className="text-retro-muted">Channel:</span>
                        <span className="text-retro-text">{streamDebugStatus.channelName || 'N/A'}</span>
                        {streamDebugStatus.title && (
                          <>
                            <span className="text-retro-muted">Title:</span>
                            <span className="text-retro-text text-xs truncate">{streamDebugStatus.title}</span>
                          </>
                        )}
                      </div>
                    ) : (
                      <p className="text-retro-muted text-sm">Stream status not available.</p>
                    )}
                  </div>

                  {/* First Today */}
                  <div className="p-3 bg-retro-surface rounded">
                    <h4 className="text-sm font-semibold text-amber-400 uppercase mb-2">First Today (FIRST! Reward)</h4>
                    {widgetDebugData.firstThisStream ? (
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <span className="text-retro-muted">Username:</span>
                        <span className="text-retro-text font-bold">{widgetDebugData.firstThisStream.username}</span>
                        <span className="text-retro-muted">Stream ID:</span>
                        <code className="text-retro-text text-xs bg-retro-bg px-1 rounded">
                          {widgetDebugData.firstThisStream.streamId || 'N/A'}
                        </code>
                        <span className="text-retro-muted">Claimed At:</span>
                        <span className="text-retro-text text-xs">
                          {widgetDebugData.firstThisStream.claimedAt
                            ? new Date(widgetDebugData.firstThisStream.claimedAt).toLocaleString()
                            : 'N/A'}
                        </span>
                      </div>
                    ) : (
                      <p className="text-yellow-300 text-sm">
                        null: No one has redeemed FIRST! this stream, or the stream is offline (stale data is hidden).
                      </p>
                    )}
                  </div>

                  {/* Check-In Leaderboard */}
                  <div className="p-3 bg-retro-surface rounded">
                    <h4 className="text-sm font-semibold text-amber-400 uppercase mb-2">
                      Check-In Leaderboard ({widgetDebugData.checkInLeaderboard?.length || 0} entries)
                    </h4>
                    {widgetDebugData.checkInLeaderboard && widgetDebugData.checkInLeaderboard.length > 0 ? (
                      <div className="space-y-1">
                        {widgetDebugData.checkInLeaderboard.map((entry, i) => (
                          <div key={i} className="flex items-center gap-3 text-sm">
                            <span className="text-retro-cyan font-bold w-6">#{i + 1}</span>
                            <span className="text-retro-text flex-1">{entry.username}</span>
                            <span className="text-amber-300 font-mono">{entry.checkIns} check-ins</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-yellow-300 text-sm">
                        Empty: No checkIns:* keys found in Redis. Check that processCheckIn() is calling updateCheckIn().
                      </p>
                    )}
                  </div>

                  {/* Top Check-In Streak */}
                  <div className="p-3 bg-retro-surface rounded">
                    <h4 className="text-sm font-semibold text-amber-400 uppercase mb-2">Top Check-In Streak</h4>
                    {widgetDebugData.firstStreak ? (
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <span className="text-retro-muted">Username:</span>
                        <span className="text-retro-text font-bold">{widgetDebugData.firstStreak.username}</span>
                        <span className="text-retro-muted">Streak:</span>
                        <span className="text-amber-300 font-bold">{widgetDebugData.firstStreak.streak} streams</span>
                      </div>
                    ) : (
                      <p className="text-yellow-300 text-sm">
                        null: No streak data found. No one has checked in yet.
                      </p>
                    )}
                  </div>

                  {/* Raw JSON */}
                  <div className="p-3 bg-retro-surface rounded">
                    <button
                      onClick={() => setDebugJsonExpanded(!debugJsonExpanded)}
                      className="text-sm font-semibold text-amber-400 uppercase hover:text-amber-300 transition-colors w-full text-left flex items-center gap-2"
                    >
                      <span>{debugJsonExpanded ? '▼' : '▶'}</span>
                      Raw API Response
                    </button>
                    {debugJsonExpanded && (
                      <pre className="mt-2 p-3 bg-retro-bg rounded text-xs text-retro-text overflow-x-auto max-h-96 overflow-y-auto font-mono whitespace-pre-wrap break-words">
                        {JSON.stringify(widgetDebugData, null, 2)}
                      </pre>
                    )}
                  </div>

                  <div className="p-3 bg-retro-bg/30 rounded text-sm text-retro-muted">
                    <p>
                      This panel shows raw data from the <code className="text-amber-400">/api/data?type=viewer-goals</code> endpoint.
                      If First Today is null, the stream may be offline or no one redeemed FIRST! yet.
                      If Check-In Leaderboard is empty, verify EventSub is registered and processCheckIn() calls updateCheckIn().
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'schedule' && (
          <div className="retro-container p-3 retro-glow">
            <p className="text-retro-muted mb-3">
              Note: To manage a specific channel's schedule, navigate to /schedule/:channelName/admin
            </p>
            <AdminSchedule24Hour channelName="itsflannelbeard" onLogout={onLogout} />
          </div>
        )}

        {activeTab === 'ideas' && (
          <>
            {/* Stats */}
            <div className="retro-container p-3 retro-glow mb-3">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-center">
                <div>
                  <div className="text-2xl font-bold text-retro-cyan">{ideas.length}</div>
                  <div className="text-sm text-retro-muted">Total Ideas</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-retro-cyan">
                    {ideas.filter(idea => idea.status === 'pending').length}
                  </div>
                  <div className="text-sm text-retro-muted">Pending</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-retro-cyan">
                    {ideas.reduce((sum, idea) => sum + (idea.votes || 0), 0)}
                  </div>
                  <div className="text-sm text-retro-muted">Total Votes</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-retro-cyan">
                    {ideas.reduce((sum, idea) => sum + ((idea.voters && idea.voters.length) || 0), 0)}
                  </div>
                  <div className="text-sm text-retro-muted">Unique Voters</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-retro-cyan">
                    {ideas.length > 0 ? Math.round(ideas.reduce((sum, idea) => sum + (idea.votes || 0), 0) / ideas.length * 10) / 10 : 0}
                  </div>
                  <div className="text-sm text-retro-muted">Avg Votes/Idea</div>
                </div>
              </div>
            </div>
        <div className="retro-container p-3 retro-glow">
          {loading && (
            <div className="text-center py-4">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-retro-cyan border-t-transparent mb-2"></div>
              <p className="retro-text text-retro-muted">Loading ideas...</p>
            </div>
          )}

          {error && (
            <div className="text-center py-4 bg-red-900/20 border-2 border-red-500 rounded-lg p-4">
              <div className="text-6xl mb-2">⚠️</div>
              <h3 className="text-xl font-bold text-red-400 mb-2">Error Loading Ideas</h3>
              <p className="text-red-300 mb-2">{error}</p>
              <button 
                onClick={fetchIdeas} 
                className="retro-button hover:scale-105 active:scale-95"
              >
                Try Again
              </button>
            </div>
          )}

          {!loading && !error && ideas.length === 0 && (
            <div className="text-center py-6">
              <h3 className="text-2xl font-bold text-retro-text mb-2">
                No Ideas Found
              </h3>
              <p className="text-retro-muted">
                No ideas have been submitted yet.
              </p>
            </div>
          )}

          {!loading && !error && ideas.length > 0 && (
            <div className="space-y-2">
              {ideas.map((idea) => (
                <div 
                  key={idea.id} 
                  className="retro-card p-3 hover:border-retro-cyan transition-all duration-200"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-grow">
                      <p className="text-lg font-bold text-retro-text mb-1 leading-tight">
                        {idea.idea}
                      </p>
                      <div className="flex items-center gap-4 text-sm text-retro-muted flex-wrap mb-2">
                        <span className="font-mono">@{idea.username}</span>
                        <span>•</span>
                        <span>{formatTimestamp(idea.timestamp)}</span>
                        <span>•</span>
                        <span className="flex items-center gap-1">
                          👍 {idea.votes || 0} votes
                          {idea.voters && idea.voters.length > 0 && (
                            <span className="text-xs">
                              ({idea.voters.length} voters)
                            </span>
                          )}
                        </span>
                        <span>•</span>
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                          idea.status === 'pending' 
                            ? 'bg-yellow-900/20 text-yellow-300 border border-yellow-500' 
                            : 'bg-green-900/20 text-green-300 border border-green-500'
                        }`}>
                          {idea.status || 'pending'}
                        </span>
                      </div>
                      
                      {/* Voter Details */}
                      {idea.voters && idea.voters.length > 0 && (
                        <div className="mb-3">
                          <button
                            onClick={() => toggleVoters(idea.id)}
                            className="text-sm text-retro-cyan hover:text-retro-cyan/80 transition-colors duration-200 flex items-center gap-1"
                          >
                            {expandedVoters.has(idea.id) ? '▼' : '▶'} 
                            View {idea.voters.length} voter{idea.voters.length !== 1 ? 's' : ''}
                            {idea.lastVoteAt && (
                              <span className="text-retro-muted">
                                (last vote: {formatVoteTime(idea.lastVoteAt)})
                              </span>
                            )}
                          </button>
                          
                          {expandedVoters.has(idea.id) && (
                            <div className="mt-2 ml-4 space-y-1 max-h-32 overflow-y-auto">
                              {idea.voters.map((voter, index) => (
                                <div key={index} className="text-xs text-retro-muted flex items-center justify-between bg-retro-bg/20 p-2 rounded">
                                  <span className="font-mono">@{voter.username}</span>
                                  <div className="flex items-center gap-2">
                                    <span>{voter.pointsSpent} pts</span>
                                    <span>•</span>
                                    <span>{formatVoteTime(voter.votedAt)}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => deleteIdea(idea.id)}
                      disabled={deletingId === idea.id}
                      className="ml-4 px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg shadow-md transition-all duration-200 hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {deletingId === idea.id ? (
                        <div className="flex items-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                          Deleting...
                        </div>
                      ) : (
                        'DELETE'
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
          </>
        )}

        {activeTab === 'discord' && (
          <div className="retro-container p-3 retro-glow">
            <AdminDiscordConfig onLogout={onLogout} />
          </div>
        )}

        {activeTab === 'steam' && (
          <div className="retro-container p-3 retro-glow">
            <div className="flex items-center justify-between mb-3">
              <h2 className="retro-title text-xl font-bold text-retro-cyan">
                STEAM LIBRARY - UNPLAYED GAMES
              </h2>
              <button
                onClick={fetchSteamGames}
                disabled={steamLoading}
                className="px-3 py-1 bg-retro-cyan text-retro-bg font-semibold rounded hover:bg-retro-cyan/80 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {steamLoading ? 'Loading...' : 'Refresh'}
              </button>
            </div>

            {steamError && (
              <div className="bg-red-900/20 border-2 border-red-500 rounded-lg p-4 mb-3">
                <p className="text-red-300 font-semibold mb-2">Error:</p>
                <p className="text-red-300">{steamError}</p>
                {steamError.includes('STEAM_API_KEY') && (
                  <p className="text-red-300 text-sm mt-2">
                    Get your API key from: <a href="https://steamcommunity.com/dev/apikey" target="_blank" rel="noopener noreferrer" className="underline">https://steamcommunity.com/dev/apikey</a>
                  </p>
                )}
                {steamError.includes('STEAM_ID64') && (
                  <p className="text-red-300 text-sm mt-2">
                    Find your SteamID64 in your profile URL: <span className="font-mono">https://steamcommunity.com/profiles/[SteamID64]/</span>
                  </p>
                )}
                {steamError.includes('private') && (
                  <p className="text-red-300 text-sm mt-2">
                    Go to your Steam privacy settings and set "Game details" to Public.
                  </p>
                )}
              </div>
            )}

            {steamLoading && (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-retro-cyan border-t-transparent mb-4"></div>
                <p className="retro-text text-retro-muted">Loading Steam games...</p>
              </div>
            )}

            {!steamLoading && !steamError && (
              <>
                {steamGames.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-retro-muted text-lg mb-2">No unplayed games found!</p>
                    <p className="text-retro-muted text-sm">You've played all the games in your library, or your library is empty.</p>
                  </div>
                ) : (
                  <>
                    <div className="mb-4">
                      <p className="text-retro-text">
                        <span className="font-bold text-retro-cyan">{steamGames.length}</span> game{steamGames.length !== 1 ? 's' : ''} with 0 hours played
                      </p>
                    </div>
                    <div className="space-y-2 max-h-[600px] overflow-y-auto">
                      {steamGames.map((game) => (
                        <div
                          key={game.appid}
                          className="retro-card p-3 border-2 border-retro-cyan hover:border-retro-cyan/80 transition-all duration-200"
                        >
                          <div className="flex items-center gap-3">
                            {game.img_logo_url && (
                              <img
                                src={`https://media.steampowered.com/steamcommunity/public/images/apps/${game.appid}/${game.img_logo_url}.jpg`}
                                alt={game.name}
                                className="w-16 h-16 object-cover rounded"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            )}
                            <div className="flex-1">
                              <h3 className="text-retro-text font-semibold text-lg">{game.name}</h3>
                              <p className="text-retro-muted text-sm">App ID: {game.appid}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        )}
      </div>
      <Toast message={toastMessage} />
    </div>
  );
}
